import {
    convertVersionsToDtos,
    convertVersionToDto,
    deserializeVersion
} from '@app/shared/versions/versions.utils';
import { deserializeDesignDocument, stringifyDesignDocument } from '@creative/serialization';
import { adjustWeightCalculation } from '@creative/utils';
import { CreativeDtoV2, CreativeSetDtoV2, DesignDtoV2, ElementDtoV2 } from '@studio/domain/api/sapi';
import {
    CreativesetSnapshotDtoV2,
    ICreativesetSnapshot,
    ICreativesetSnapshotDto,
    isCreativesetSnapshotDtoV2,
    SnapshotCreativeDto,
    SnapshotDesignDto,
    SnapshotElementDto
} from '@studio/domain/creativeset';
import { ApprovalStatus, ICreative, ICreativeDto } from '@studio/domain/creativeset/creative';
import { ICreativeset } from '@studio/domain/creativeset/creativeset';
import { IDesign, IDesignDto } from '@studio/domain/creativeset/design';
import { IElement } from '@studio/domain/creativeset/element';
import { IVersion, IVersionDto } from '@studio/domain/creativeset/version';
import { deserializeImageAssets } from './asset';
import { deserializeFontFamilies } from './font-families';

export function deserializeCreativeset(
    creativesetDto: CreativeSetDtoV2,
    creativeId?: string
): ICreativeset {
    const { creatives, designs, versions, elements } = deserializeCreativeData(
        creativesetDto,
        creativeId
    );
    const defaultVersion = deserializeVersion(creativesetDto.defaultVersion);
    const images = deserializeImageAssets(creativesetDto.images);

    return {
        id: creativesetDto.id,
        name: creativesetDto.name,
        brandId: creativesetDto.brandId,
        stateId: creativesetDto.stateId,
        creatives,
        designs,
        versions,
        defaultVersion,
        sizes: creativesetDto.sizes,
        elements,
        images,
        videos: creativesetDto.videos,
        widgets: creativesetDto.widgets
    };
}

export function deserializeCreativesetSnapshot(
    creativesetSnapshotDto: ICreativesetSnapshotDto | CreativesetSnapshotDtoV2,
    creativeId?: string
): ICreativesetSnapshot {
    let creativesetDto: CreativeSetDtoV2;
    if (isCreativesetSnapshotDtoV2(creativesetSnapshotDto)) {
        creativesetDto = convertSnapshotToCreativesetV2(creativesetSnapshotDto);
    } else {
        creativesetDto = {
            ...creativesetSnapshotDto,
            creatives: convertCreativesV1ToV2(creativesetSnapshotDto.creatives),
            designs: convertDesignsV1ToV2(creativesetSnapshotDto.designs),
            elements: convertElementsV1ToV2(creativesetSnapshotDto.elements)
        };
    }
    return {
        ...deserializeCreativeset(creativesetDto, creativeId),
        fontFamilies: deserializeFontFamilies((creativesetSnapshotDto.fontFamilies as any) || [])
    };
}

export function convertSnapshotToCreativesetV2(
    creativesetSnapshotDto: CreativesetSnapshotDtoV2
): CreativeSetDtoV2 {
    const defaultVersion = creativesetSnapshotDto.versions.find(
        ({ id }) => id === creativesetSnapshotDto.defaultVersion.id
    );
    if (!defaultVersion) {
        throw new Error(`Default version not found: ${creativesetSnapshotDto.defaultVersion.id}`);
    }

    return {
        ...creativesetSnapshotDto,
        defaultVersion: defaultVersion,
        creatives: creativesetSnapshotDto.creatives.map(creative =>
            convertSnapshotV2Creative(creative, creativesetSnapshotDto)
        )
    };
}

function convertSnapshotV2Creative(
    creative: SnapshotCreativeDto,
    creativesetSnapshotDto: CreativesetSnapshotDtoV2
): CreativeDtoV2 {
    return {
        ...creative,
        design: convertSnapshotV2Design(
            creative.design,
            creativesetSnapshotDto.designs,
            creativesetSnapshotDto.elements
        )
    };
}

function convertSnapshotV2Design(
    design: { id: string } | undefined,
    designs: SnapshotDesignDto[],
    elements: SnapshotElementDto[]
): DesignDtoV2 | undefined {
    if (!design) {
        return undefined;
    }
    const designDto = designs.find(({ id }) => id === design.id);
    if (!designDto) {
        throw new Error(`Design not found: ${design.id}`);
    }
    const designElements = elements.filter(({ id }) =>
        designDto.elements.some(({ id: designElementId }) => designElementId === id)
    );
    return {
        ...designDto,
        elements: designElements
    };
}

export function deserializeCreativeData(
    creativesetDto: CreativeSetDtoV2,
    creativeId?: string
): { creatives: ICreative[]; designs: IDesign[]; versions: IVersion[]; elements: IElement[] } {
    let creatives: ICreative[];
    let designs: IDesign[];
    let versions: IVersion[];
    const elements = deserializeCreativesetElements(creativesetDto.elements);

    if (creativeId) {
        // just deserialize data for one creative
        const creative = creativesetDto.creatives.find(c => c.id === creativeId);
        if (!creative) {
            throw new Error('Creative not found');
        }

        const design = creativesetDto.designs.find(({ id }) => id === creative.design?.id);
        const parsedCreative = deserializeCreative(
            creativesetDto,
            creative,
            design ? [deserializeDesign(design, elements)] : []
        );

        creatives = [parsedCreative];
        designs = parsedCreative.design ? [parsedCreative.design] : [];
        versions = [parsedCreative.version];
    } else {
        designs = creativesetDto.designs.map(design => deserializeDesign(design, elements));
        creatives = creativesetDto.creatives.map(creative =>
            deserializeCreative(creativesetDto, creative, designs)
        );
        versions = creativesetDto.versions.map(version => deserializeVersion(version));
    }

    return { creatives, designs, versions, elements };
}

export function deserializeCreative(
    creativesetDto: CreativeSetDtoV2,
    creativeDto: CreativeDtoV2 | ICreativeDto,
    designs: IDesign[]
): ICreative {
    const { sizes, versions } = creativesetDto;

    const design = creativeDto.design ? designs.find(d => d.id === creativeDto.design?.id) : undefined; // creative can have no design
    const size = sizes.find(s => s.id === creativeDto.size.id);
    const version = versions.find(v => v.id === creativeDto.version.id);

    if (!size) {
        throw new Error('No size');
    }

    if (!version) {
        throw new Error('No version');
    }

    // Graphql property
    removeGraphQlTypename(creativeDto);

    const { id, checksum, targetUrl } = creativeDto;

    return {
        id,
        checksum,
        targetUrl,
        design,
        size,
        version: Object.freeze(deserializeVersion(version)),
        approvalStatus: (creativeDto.approvalStatus as ApprovalStatus) ?? ApprovalStatus.None,
        connectedCampaigns: creativeDto.connectedCampaigns,
        creativeWeights: creativeDto.creativeWeights
            ? adjustWeightCalculation(creativeDto.creativeWeights)
            : undefined
    };
}

function deserializeDesign(design: DesignDtoV2, elements: IElement[]): IDesign {
    const designDocument = deserializeDesignDocument(design.document);
    return {
        id: design.id,
        name: design.name,
        hasHeavyVideo: design.hasHeavyVideo,
        elements: elements.filter(({ id: elementId }) =>
            design.elements.some(({ id: designElementId }) => designElementId === elementId)
        ),
        document: designDocument
    };
}

export function deserializeCreativesetElements(elements: ElementDtoV2[]): IElement[] {
    return elements.map(deserializeCreativesetElement);
}

export function deserializeCreativesetElement(element: ElementDtoV2): IElement {
    removeGraphQlTypename(element);
    element.properties.forEach(property => {
        removeGraphQlTypename(property);
    });

    return {
        ...element,
        type: element.type as IElement['type']
    };
}

export function removeGraphQlTypename(obj: object): void {
    delete obj['__typename'];
}

export function convertCreativesetToDto(creativeSet: ICreativeset): CreativeSetDtoV2 {
    const { id, name, brandId, images, videos, widgets, sizes, stateId, elements } = creativeSet;

    const designs: IDesignDto[] = creativeSet.designs.map(design => ({
        ...design,
        document: stringifyDesignDocument(design.document)
    }));

    const versions: IVersionDto[] = convertVersionsToDtos(creativeSet.versions);
    const creatives: CreativeDtoV2[] = convertCreativesV1ToV2(creativeSet.creatives);
    const defaultVersion = convertVersionToDto(creativeSet.defaultVersion);

    // We can spread creativeset when the Creativeset data class is removed
    return {
        id,
        name,
        brandId,
        images,
        videos,
        widgets,
        sizes,
        stateId,
        elements: convertElementsV1ToV2(elements),
        designs: convertDesignsV1ToV2(designs),
        versions,
        creatives,
        defaultVersion
    };
}

export function convertElementsV1ToV2(elements: IElement[]): ElementDtoV2[] {
    return elements.map(element => ({
        ...element,
        type: element.type as ElementDtoV2['type'],
        properties: element.properties.map(property => {
            return {
                ...property,
                unit: convertUnitsV1ToV2(property.unit)
            };
        })
    }));
}

const validUnits = [
    'array',
    'boolean',
    'color',
    'feed',
    'font',
    'id',
    'image',
    'number',
    'object',
    'select',
    'string',
    'text'
] as const;
type UnitType = (typeof validUnits)[number];

export function convertUnitsV1ToV2(unit?: string): UnitType | undefined {
    return validUnits.includes(unit as UnitType) ? (unit as UnitType) : undefined;
}

export function convertDesignsV1ToV2(designs: IDesignDto[]): DesignDtoV2[] {
    return designs.map(design => ({
        ...design,
        document: JSON.parse(design.document)
    }));
}

export function convertCreativesV1ToV2(creatives: ICreativeDto[]): CreativeDtoV2[] {
    return creatives.map(creative => convertCreativeV1ToV2(creative));
}

export function convertCreativeV1ToV2(creative: ICreativeDto): CreativeDtoV2 {
    let mappedStatus: CreativeDtoV2['approvalStatus'];
    switch (creative.approvalStatus) {
        case ApprovalStatus.InProgress:
            mappedStatus = 'In progress';
            break;
        case ApprovalStatus.ForReview:
            mappedStatus = 'For review';
            break;
        case ApprovalStatus.NotApproved:
            mappedStatus = 'Not approved';
            break;
        case ApprovalStatus.Approved:
            mappedStatus = 'Approved';
            break;
        default:
            mappedStatus = undefined;
    }
    return {
        id: creative.id,
        connectedCampaigns: creative.connectedCampaigns,
        checksum: creative.checksum,
        creativeWeights: creative.creativeWeights,
        targetUrl: creative.targetUrl,
        design: creative.design ? { id: creative.design.id } : undefined,
        version: { id: creative.version.id },
        size: { id: creative.size.id },
        approvalStatus: mappedStatus
    };
}

export function mapCreativeSizes(creativeset: ICreativeset): ICreative[] {
    return creativeset.creatives.map(creative => {
        creative.size = creativeset.sizes.find(size => size.id === creative.size.id)!;
        return creative;
    });
}

export function mapCreatives(creativeset: ICreativeset): ICreative[] {
    return creativeset.creatives.map(creative => {
        creative.design = creativeset.designs.find(design => design.id === creative.design?.id)!;
        return creative;
    });
}
